
import React, { useEffect, useState } from 'react';
import { userService } from '../../services/user.service';
import { KycStatus as KycStatusType } from '../../types';
import Badge from '../../components/common/Badge';

const KycStatusSkeleton = () => (
  <div className="max-w-2xl mx-auto space-y-10 animate-pulse">
    <div className="h-10 w-64 bg-slate-800 rounded-xl mx-auto"></div>
    <div className="h-80 bg-slate-900/50 rounded-[2.5rem] border border-slate-800"></div>
  </div>
);

const KycStatus: React.FC = () => {
  const [kyc, setKyc] = useState<KycStatusType | null>(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [error, setError] = useState('');

  const fetchKyc = async () => {
    try {
      const response = await userService.getKycStatus();
      setKyc(response.data || response);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchKyc();
  }, []);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setPreview(reader.result as string);
      reader.readAsDataURL(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;
    setUploading(true);
    setError('');
    try {
      const response = await userService.uploadKyc(selectedFile);
      if (response.status) {
        await fetchKyc();
        setSelectedFile(null);
        setPreview(null);
      } else {
        setError(response.message || 'Handshake failed.');
      }
    } catch (err: any) {
      setError(err.message || 'Signal lost during transmission.');
    } finally {
      setUploading(false);
    }
  };

  if (loading) return <KycStatusSkeleton />;

  // 0: Pending, 1: Approved, 2: Rejected
  const status = kyc?.verifyStatus;
  const canUpload = !status || status === "2";

  return (
    <div className="max-w-2xl mx-auto space-y-10 animate-in fade-in slide-in-from-bottom-4 duration-700 pb-20">
      <div className="text-center">
        <h2 className="text-3xl font-black text-white uppercase tracking-tight">Identity Clearance</h2>
        <p className="text-slate-500 text-[10px] font-black uppercase tracking-[0.3em] mt-1">Biometric & Document verification terminal</p>
      </div>

      <div className="bg-slate-900/30 backdrop-blur-xl p-10 rounded-[2.5rem] border border-slate-800/50 shadow-2xl">
        {status === "0" ? (
          <div className="py-12 text-center">
            <div className="w-24 h-24 bg-amber-500/10 rounded-full flex items-center justify-center mx-auto mb-8 border border-amber-500/20 shadow-inner">
              <svg className="w-10 h-10 text-amber-500 animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
            </div>
            <h3 className="text-2xl font-black text-white uppercase tracking-tight">Handshake Pending</h3>
            <p className="text-slate-500 text-sm font-medium mt-4 max-w-sm mx-auto leading-relaxed">Your identity clearance documents are currently being audited by our core security team. Estimated completion: <span className="text-white">12-24 hours</span>.</p>
          </div>
        ) : status === "1" ? (
          <div className="py-12 text-center">
            <div className="w-24 h-24 bg-emerald-500/10 rounded-full flex items-center justify-center mx-auto mb-8 border border-emerald-500/20 shadow-[0_0_20px_rgba(16,185,129,0.1)]">
              <svg className="w-12 h-12 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg>
            </div>
            <h3 className="text-2xl font-black text-white uppercase tracking-tight">Clearance Granted</h3>
            <p className="text-slate-400 text-sm font-medium mt-4">Profile state: <span className="text-emerald-500 font-black">VERIFIED</span>. Global distribution network enabled.</p>
          </div>
        ) : (
          <div className="space-y-8">
            {status === "2" && (
              <div className="bg-rose-500/10 p-6 rounded-2xl border border-rose-500/30 mb-8">
                <div className="flex items-center space-x-3 mb-2">
                  <svg className="w-5 h-5 text-rose-500" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" /></svg>
                  <p className="text-rose-400 text-[11px] font-black uppercase tracking-widest">Clearance Revoked</p>
                </div>
                <p className="text-slate-400 text-sm font-bold">Audit result: "{kyc?.rejectReason || 'Document clarity issues detected.'}"</p>
              </div>
            )}

            <div className="text-center">
              <p className="text-slate-400 text-sm font-medium mb-8 leading-relaxed">Upload a high-resolution identification document (Passport, ID, or Driver's License) to establish your secure identity clearance.</p>
              
              <label className="block group cursor-pointer">
                <div className={`border-2 border-dashed rounded-[2rem] p-12 transition-all duration-300 flex flex-col items-center ${preview ? 'border-purple-500 bg-purple-500/5' : 'border-slate-800 bg-slate-950/30 hover:border-slate-700 hover:bg-slate-950/50'}`}>
                  {preview ? (
                    <img src={preview} alt="Document Preview" className="max-h-64 rounded-2xl shadow-2xl animate-in zoom-in-95 duration-500" />
                  ) : (
                    <>
                      <div className="w-16 h-16 bg-slate-900 rounded-2xl flex items-center justify-center mb-6 border border-slate-800 shadow-inner group-hover:scale-110 transition-transform">
                        <svg className="w-8 h-8 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" /></svg>
                      </div>
                      <span className="text-slate-500 text-xs font-black uppercase tracking-widest">Select Source File</span>
                      <span className="text-[10px] text-slate-600 mt-2 uppercase tracking-tight">JPG, PNG, or PDF supported</span>
                    </>
                  )}
                </div>
                <input type="file" className="hidden" accept="image/*,.pdf" onChange={handleFileChange} />
              </label>

              {error && (
                <div className="mt-6 p-4 bg-rose-500/10 border border-rose-500/20 text-rose-500 text-[10px] font-black uppercase tracking-widest rounded-xl">
                  {error}
                </div>
              )}

              <button 
                disabled={!selectedFile || uploading}
                onClick={handleUpload}
                className="w-full mt-10 bg-purple-600 hover:bg-purple-500 text-white font-black py-5 rounded-2xl transition-all shadow-xl shadow-purple-600/30 active:scale-95 disabled:opacity-30 disabled:grayscale uppercase tracking-[0.2em] text-xs"
              >
                {uploading ? 'UPLOADING DATA...' : 'TRANSMIT CLEARANCE DATA'}
              </button>
            </div>
          </div>
        )}
      </div>

      {kyc?.document && status === "1" && (
        <div className="bg-slate-900/20 p-6 rounded-[2rem] border border-slate-800/40 text-center">
          <p className="text-[10px] font-black text-slate-600 uppercase tracking-widest mb-6">Archive Preview</p>
          <img src={kyc.document} alt="KYC" className="w-full rounded-2xl grayscale opacity-30 hover:grayscale-0 hover:opacity-100 transition-all cursor-pointer duration-500 max-h-48 object-contain" />
        </div>
      )}
    </div>
  );
};

export default KycStatus;
